import { ActivityEnrollableTimesModal_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  TutorMatchEnrollmentDataContext,
  TutorMatchEnrollmentDataType,
  useShowCredits
} from "@outschool/ui-components-website";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import { useNavigation, useNodeRef } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback as useDebouncedCallbackVendor } from "use-debounce";

import { EnrollmentFromStripePurchaseOrder } from "../../../components/enrolling/EnrollmentMeetingPurchaseWithStripe";
import TutorMatchActivityEnrollPage from "./TutorMatchActivityEnrollPage";

type TutorEnrollButtonAndModal = {
  activity: {
    uid: string;
    isSelfPaced?: boolean;
    subject: string | null;
    size_max: number | null;
  };
  buttonText: string;
  buttonSize?: "small" | "medium" | "large";
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
};

export default function TutorEnrollButtonAndModal({
  activity,
  buttonText,
  buttonSize = "medium",
  activityWithEnrollableTimes
}: TutorEnrollButtonAndModal) {
  const [tutorMatchEnrollmentData, setTutorMatchEnrollmentData] =
    React.useState<TutorMatchEnrollmentDataType | undefined>(undefined);

  const [tutorMatchActivityEnrollNode, tutorMatchActivityEnrollRef] =
    useNodeRef();
  const location = useLocation();
  const navigate = useNavigation();
  const showCredits = useShowCredits();
  const { currentUser, isLoggedIn } = useSession();
  const { trackTouch: trackTutorMatchEnroll } = useImpressionTracking({
    node: tutorMatchActivityEnrollNode,
    trackingLabel: "tutor_match_page_activity_enroll",
    sharedProperties: {
      activity_uid: tutorMatchEnrollmentData?.activity.uid,
      sectionUid: tutorMatchEnrollmentData?.section?.uid
    },
    uniqueId: `tutor_match_page_activity_enroll-${tutorMatchEnrollmentData?.activity.uid}`
  });
  const [tutorMatchActivityEnrollmentOnComplete] = useDebouncedCallbackVendor(
    async (enrollments: EnrollmentFromStripePurchaseOrder[]) => {
      if (tutorMatchEnrollmentData) {
        const enrollment = enrollments[0];
        if (enrollment) {
          // track learner planner enrollment
          trackTutorMatchEnroll({
            enrollmentUid: enrollment.uid,
            activityUid: tutorMatchEnrollmentData.activity.uid,
            sectionUid: enrollment.section_uid
          });
        }

        enrollment &&
          setTutorMatchEnrollmentData?.({
            ...tutorMatchEnrollmentData,
            section: { uid: enrollment?.section_uid! }
          });

        return Promise.resolve();
      }
    },
    500
  );

  const handleTutorEnrollClick = () => {
    if (isLoggedIn) {
      if (showCredits && !currentUser?.hasSubscription) {
        const queryParams = new URLSearchParams(location.search);
        // eslint-disable-next-line i18next/no-literal-string
        queryParams.set("joinMembershipModal", "true");
        navigate(`${location.pathname}?${queryParams.toString()}`, {
          replace: true
        });
      } else {
        setTutorMatchEnrollmentData?.({
          activity
        });
      }
    } else {
      setTutorMatchEnrollmentData?.({
        activity
      });
    }
  };

  return (
    <TutorMatchEnrollmentDataContext.Provider
      value={{
        tutorMatchEnrollmentData,
        setTutorMatchEnrollmentData
      }}
    >
      <TrackedButton
        variant="contained"
        fullWidth
        size={buttonSize}
        onClick={handleTutorEnrollClick}
        trackingName={"tutor_calendar_enrollment_button"}
        trackingUniqueId={activity.uid}
        trackingSharedProperties={{
          activity_uid: activity.uid
        }}
      >
        {buttonText}
      </TrackedButton>
      {tutorMatchEnrollmentData && (
        <TutorMatchActivityEnrollPage
          ref={tutorMatchActivityEnrollRef}
          // NOTE: Technically don't need these anymore since we are passing them in context provider.
          tutorMatchEnrollmentData={tutorMatchEnrollmentData}
          onCancel={() => {
            setTutorMatchEnrollmentData(undefined);
          }}
          onPaymentComplete={tutorMatchActivityEnrollmentOnComplete}
          activityWithEnrollableTimes={activityWithEnrollableTimes}
        />
      )}
    </TutorMatchEnrollmentDataContext.Provider>
  );
}
